import { Injectable } from '@angular/core';
import {
  BlobEntity,
  FileService,
  ZipService,
} from '@epicuro-next/ataraxia/file';
import {
  OrderDetailsService,
  OrderHeader,
  OrdersService,
} from '@epicuro-next/data/order-book';
import { ProductService } from '@epicuro-next/data/product';
import {
  FiltersFieldIds,
  IOrderProduct,
} from '@epicuro-next/models/order-book';
import { selectActiveAccountId } from '@epicuro-next/state/accounts-state';
import {
  selectQueryParams,
  selectRouteParams,
} from '@epicuro-next/state/router-store';
import { MessageType, showSnackBarMessage } from '@epicuro-next/state/snackbar';
import { isNonNullish, isNullish } from '@epicuro-next/utilities';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

import { mapDetailsDropShipAddress } from '../../shared/map-details-drop-ship-address';
import { reduceOrders } from '../../shared/reduce-orders/reduce-orders';
import * as fromActions from '../actions/order-details.actions';
import {
  selectOrderInvoices,
  selectOrderReferenceNumber,
  selectOrderSalesNumber,
} from '../selectors/order-details.selectors';

const ORDER_DETAILS_FILTER_FIELD_KEY = 'Order_salesOrderNumber';

@Injectable()
export class OrderDetailsEffects {
  private accountIds$: Observable<string[]> = this.store.pipe(
    select(selectQueryParams),
    map((params) => params[FiltersFieldIds.AccountId]),
    map((accountIds?: string | string[]) => {
      if (accountIds) {
        return Array.isArray(accountIds) ? accountIds : [accountIds];
      }

      return [];
    }),
  );

  constructor(
    private actions$: Actions,
    private orderDetailsService: OrderDetailsService,
    private productService: ProductService,
    private ordersService: OrdersService,
    private store: Store,
    private fileService: FileService,
    private zipService: ZipService,
  ) {}

  public loadOrderDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.loadOrderDetails),
      concatLatestFrom(() => [
        this.store.pipe(select(selectRouteParams)),
        this.accountIds$,
        this.store.pipe(select(selectActiveAccountId), filter(isNonNullish)),
      ]),
      switchMap(([_, routeParams, accountIds, activeAccountId]) => {
        const filterItem = {
          filters: [
            {
              fieldId: ORDER_DETAILS_FILTER_FIELD_KEY,
              values: [routeParams.orderNumber],
            },
            {
              fieldId: FiltersFieldIds.AccountId,
              values: accountIds,
            },
          ],
        };

        return forkJoin([
          this.ordersService.getOrders(filterItem).pipe(map(reduceOrders)),
          this.orderDetailsService.getOrderItems(
            routeParams.orderNumber,
            accountIds,
          ),
        ]).pipe(
          tap(([details]) => {
            if (isNullish(details))
              throw new Error('Order is empty or invalid');
          }),
          filter(
            ([details, orderItems]) =>
              isNonNullish(orderItems[0]?.brandId) &&
              isNonNullish(details?.account?.externalId),
          ),
          switchMap(([details, orderItems]) => {
            const brandId = orderItems[0].brandId as string;
            const accountId = details.account?.id as string;
            const productIds = orderItems.map((item) => item.id);
            return forkJoin([
              this.productService
                .getOrderProducts(brandId, accountId, productIds)
                .pipe(
                  catchError((err) => {
                    console.error(
                      `Failed to load products info: ${err.message}`,
                    );
                    return of([]);
                  }),
                ),
              this.orderDetailsService.getOrderHeader(
                details.salesOrderNumber,
                activeAccountId,
                details.account?.id as string,
              ),
            ]).pipe(
              map(
                ([products, orderHeader]: [IOrderProduct[], OrderHeader]) => ({
                  details: mapDetailsDropShipAddress(details, orderHeader),
                  orderItems,
                  products,
                  orderHeader,
                }),
              ),
            );
          }),
          map(({ details, orderItems, products, orderHeader }) =>
            fromActions.loadOrderDetailsSuccess({
              details,
              orderItems,
              products,
              orderHeader,
            }),
          ),
          catchError((error) => [
            fromActions.loadOrderDetailsFailure({ error: error.message }),
          ]),
        );
      }),
    ),
  );

  public openPrintWhenAllLinesLoaded$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.printOrderDetails),
        tap(() => {
          setTimeout(() => {
            window.print();
          }, 100); // timeout to let everything be rendered before printing
        }),
      ),
    {
      dispatch: false,
    },
  );

  public triggerDownloadOrderInvoices$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.downloadOrderInvoices),
      concatLatestFrom(() => this.store.pipe(select(selectOrderInvoices))),
      switchMap(([, invoices]) =>
        this.fileService
          .getFilesFromUrl(
            invoices.map(({ invoiceUrl }) => ({
              url: invoiceUrl,
            })),
          )
          .pipe(
            switchMap((blobEntities) => [
              fromActions.downloadOrderInvoicesSuccess({
                entities: blobEntities.filter(({ blob }) =>
                  isNonNullish(blob),
                ) as Required<BlobEntity>[],
              }),
              fromActions.downloadOrderInvoicesFailure({
                failedFiles: blobEntities
                  .filter(({ blob }) => isNullish(blob))
                  .map(({ filename }) => filename),
              }),
            ]),
          ),
      ),
    ),
  );

  public processDownloads$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.downloadOrderInvoicesSuccess),
        filter(({ entities }) => !!entities.length),
        switchMap(({ entities }) => this.zipService.fromBlobEntities(entities)),
        concatLatestFrom(() => [
          this.store.pipe(select(selectOrderSalesNumber)),
          this.store.pipe(select(selectOrderReferenceNumber)),
        ]),
        tap(([blob, orderSalesNumber, orderReference]) =>
          this.fileService.saveBlob(
            blob,
            `${orderSalesNumber} ${orderReference}`,
          ),
        ),
      ),
    { dispatch: false },
  );

  public handleFailedDownloads$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.downloadOrderInvoicesFailure),
      filter(({ failedFiles }) => !!failedFiles.length),
      map(({ failedFiles }) => {
        const message = $localize`:orderBook|Download invoices error message@@downloadInvoicesErrorMessage:Error occured during download`;

        return showSnackBarMessage({
          message: `${message}: ${failedFiles.join(', ')}`,
          messageType: MessageType.DANGER,
        });
      }),
    ),
  );
}
